import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import * as api from "../api/client";
import type {
  AdminJob,
  AdminNpcDeck,
  AdminPool,
  CatalogCard,
  NpcDeckCatalogCard,
} from "../api/client";
import { AppHeader, Breadcrumb } from "../components/AppHeader";
import { AnimatedEllipsis } from "../components/admin/AnimatedEllipsis";
import {
  CardBrowser,
  CardBrowserSelect,
  type CardBrowserFilters,
} from "../components/CardBrowser";
import { Inspector } from "../components/Inspector";
import { formatChangesetErrors } from "../logic/changesetErrors";
import {
  DECK_SIZE,
  FACTIONS,
  type Faction,
} from "../logic/deckBuilder";
import { slugify } from "../logic/slug";
import { stringCodec, useUrlSyncedState } from "../logic/useFilterState";
import { factionFor } from "../theme/factions";
import { defaultBtn, primaryBtn, withBtnStyle } from "../theme/styles";
import { RARITIES } from "../theme/rarities";
import { pixelTheme, type Theme } from "../theme/pixel";
import { useResponsiveLayout } from "../logic/useResponsiveLayout";
import { E32 } from "../theme/endesga32";
import { PaginationControls, prettyPoolName, type AdminCardSort } from "./AdminPage";

const PER_PAGE = 50;

interface DeckEntry {
  card: NpcDeckCatalogCard;
  count: number;
}

export function NpcDeckEditorPage() {
  const theme = pixelTheme;
  const navigate = useNavigate();
  const { narrow } = useResponsiveLayout();
  const params = useParams<{ slug: string }>();
  const slug = params.slug ?? "";

  const [user, setUser] = useState<api.User | null>(null);
  const [deck, setDeck] = useState<AdminNpcDeck | null>(null);
  const [catalog, setCatalog] = useState<NpcDeckCatalogCard[]>([]);
  const [pools, setPools] = useState<string[]>([]);
  const [page, setPage] = useState(1);
  const [totalCards, setTotalCards] = useState(0);
  const [browserFilters, setBrowserFilters] = useState<CardBrowserFilters>({
    q: "",
    factions: ["dwarves", "elves", "goblins", "humans", "orcs"],
    type: "all",
  });
  const [name, setName] = useState("");
  const [faction, setFaction] = useState<Faction>("humans");
  const [entries, setEntries] = useState<DeckEntry[]>([]);
  const [loadingDeck, setLoadingDeck] = useState(true);
  const [loadingCatalog, setLoadingCatalog] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [inspect, setInspect] = useState<NpcDeckCatalogCard | null>(null);

  // Pool/Sort/Claim filters mirror /admin/cards. URL-synced so an
  // admin can deep-link to "humans, npc_cards pool, sorted by newest".
  const poolCodec = useMemo(() => stringCodec<AdminPool | "all">("all"), []);
  const [poolFilter, setPoolFilter] = useUrlSyncedState<AdminPool | "all">(
    "pool",
    "all",
    true,
    poolCodec,
  );
  const sortCodec = useMemo(() => stringCodec<AdminCardSort>("cost"), []);
  const [sortMode, setSortMode] = useUrlSyncedState<AdminCardSort>(
    "sort",
    "cost",
    true,
    sortCodec,
  );
  const claimCodec = useMemo(
    () => stringCodec<"all" | "unclaimed">("all"),
    [],
  );
  const [claimFilter, setClaimFilter] = useUrlSyncedState<"all" | "unclaimed">(
    "claim",
    "all",
    true,
    claimCodec,
  );
  // cardId → most recent regen_image job. Drives the inspector's
  // GENERATING animation and image cache-bust on success.
  const [regenJobs, setRegenJobs] = useState<Record<string, AdminJob>>({});
  const [regenSubmitting, setRegenSubmitting] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      try {
        const me = await api.getMe();
        setUser(me.user);
      } catch {
        // Header degrades to logged-out state; admin gate kicks in below.
      }
    })();
  }, []);

  // Deck load: once per slug. Entries come from the server-inlined
  // `card` field on each entry, so the deck panel doesn't depend on
  // the (paginated) catalog.
  useEffect(() => {
    if (!slug) return;
    setLoadingDeck(true);
    void (async () => {
      try {
        const { deck } = await api.getAdminNpcDeck(slug);
        const loaded: DeckEntry[] = [];
        for (const { card_id, count, card } of deck.cards) {
          if (card) loaded.push({ card, count });
          else
            console.warn(
              `Deck "${slug}" references missing card "${card_id}"; skipping.`,
            );
        }
        setDeck(deck);
        setName(deck.name);
        const f = isFactionString(deck.faction) ? deck.faction : "humans";
        setFaction(f);
        setEntries(loaded);
        setError(null);
      } catch (err) {
        if (err instanceof api.ApiError && err.status === 404) {
          setError(`No NPC deck with slug "${slug}".`);
        } else {
          setError(err instanceof Error ? err.message : String(err));
        }
      } finally {
        setLoadingDeck(false);
      }
    })();
  }, [slug]);

  const onBrowserFiltersChange = useCallback((next: CardBrowserFilters) => {
    setBrowserFilters((prev) =>
      prev.q === next.q &&
      prev.type === next.type &&
      prev.factions.join(",") === next.factions.join(",")
        ? prev
        : next,
    );
  }, []);

  // Catalog load: page + filter driven, server-paginated. Identical
  // shape to AdminCardsTab so the same backend filter/sort handles both.
  const loadCatalog = useCallback(async () => {
    setLoadingCatalog(true);
    try {
      const { cards, pools, total } = await api.getAdminNpcDeckCatalog({
        page,
        per_page: PER_PAGE,
        pool: poolFilter,
        claim: claimFilter,
        sort: sortMode,
        q: browserFilters.q,
        factions: browserFilters.factions.join(","),
        type: browserFilters.type,
      });
      setCatalog(cards);
      setPools(pools);
      setTotalCards(total);
    } catch (err) {
      if (err instanceof api.ApiError && err.status === 401) {
        navigate("/login", { replace: true });
        return;
      }
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoadingCatalog(false);
    }
  }, [navigate, page, poolFilter, claimFilter, sortMode, browserFilters]);

  useEffect(() => {
    void loadCatalog();
  }, [loadCatalog]);

  // Reset to page 1 whenever filters/sort change.
  useEffect(() => {
    setPage(1);
  }, [poolFilter, claimFilter, sortMode, browserFilters]);

  const deckCounts = useMemo<Record<string, number>>(() => {
    const m: Record<string, number> = {};
    for (const e of entries) m[e.card.id] = e.count;
    return m;
  }, [entries]);

  const total = useMemo(
    () => entries.reduce((acc, e) => acc + e.count, 0),
    [entries],
  );

  const newSlugPreview = useMemo(() => {
    const next = slugify(name);
    return next === slug ? null : next;
  }, [name, slug]);

  const validationIssues = useMemo(() => {
    const issues: string[] = [];
    if (name.trim().length === 0) issues.push("Name required.");
    if (total !== DECK_SIZE) issues.push(`Deck has ${total}/${DECK_SIZE} cards.`);
    if (newSlugPreview && newSlugPreview.length === 0) {
      issues.push("Name produces an empty slug.");
    }
    return issues;
  }, [name, total, newSlugPreview]);
  const canSave = validationIssues.length === 0 && !saving;

  const addCopy = useCallback((c: NpcDeckCatalogCard) => {
    setEntries((prev) => {
      const idx = prev.findIndex((e) => e.card.id === c.id);
      if (idx < 0) return [...prev, { card: c, count: 1 }];
      const next = prev.slice();
      next[idx] = { ...next[idx], count: next[idx].count + 1 };
      return next;
    });
  }, []);

  const removeCopy = useCallback((cardId: string) => {
    setEntries((prev) => {
      const idx = prev.findIndex((e) => e.card.id === cardId);
      if (idx < 0) return prev;
      const next = prev.slice();
      if (next[idx].count <= 1) next.splice(idx, 1);
      else next[idx] = { ...next[idx], count: next[idx].count - 1 };
      return next;
    });
  }, []);

  const npcDeckAddPolicy = useCallback(() => ({ ok: true as const }), []);

  const catalogById = useMemo(() => {
    const m = new Map<string, NpcDeckCatalogCard>();
    for (const c of catalog) m.set(c.id, c);
    return m;
  }, [catalog]);

  // Adapter so CardBrowser callbacks (typed as CatalogCard) can find
  // the richer NpcDeckCatalogCard back. Mirrors AdminCardsTab's
  // `adminById.get(c.id)` pattern.
  const onInspectFromBrowser = useCallback(
    (c: CatalogCard) => {
      const full = catalogById.get(c.id);
      if (full) setInspect(full);
    },
    [catalogById],
  );
  const onAddFromBrowser = useCallback(
    (c: CatalogCard) => {
      const full = catalogById.get(c.id);
      if (full) addCopy(full);
    },
    [catalogById, addCopy],
  );

  const browserExtras = (
    <>
      <CardBrowserSelect
        theme={theme}
        label="Pool"
        value={poolFilter}
        onChange={(v) => setPoolFilter(v as AdminPool | "all")}
        options={[
          ["all", "All pools"] as [string, string],
          ...pools.map((p) => [p, prettyPoolName(p)] as [string, string]),
        ]}
      />
      <CardBrowserSelect
        theme={theme}
        label="Sort"
        value={sortMode}
        onChange={(v) => setSortMode(v as AdminCardSort)}
        options={[
          ["cost", "Cost"],
          ["created", "Newest"],
        ]}
      />
      <CardBrowserSelect
        theme={theme}
        label="Claim"
        value={claimFilter}
        onChange={(v) => setClaimFilter(v as "all" | "unclaimed")}
        options={[
          ["all", "All cards"],
          ["unclaimed", "Unclaimed only"],
        ]}
      />
    </>
  );

  const lastRegenJobsRef = useRef<Record<string, AdminJob>>({});
  useEffect(() => {
    lastRegenJobsRef.current = regenJobs;
  }, [regenJobs]);

  const refreshRegenJobs = useCallback(async () => {
    try {
      const { jobs } = await api.listAdminJobs();
      const next: Record<string, AdminJob> = {};
      for (const j of jobs) {
        if (j.kind !== "regen_image") continue;
        const id = typeof j.args?.id === "string" ? (j.args.id as string) : null;
        if (!id) continue;
        // jobs come back newest-first; first hit per card wins.
        if (!next[id]) next[id] = j;
      }
      setRegenJobs(next);

      // For any card whose latest regen-image flipped to "succeeded"
      // since the last poll, bump its image_url with `?v=N` so the
      // browser drops the stale PNG.
      const prev = lastRegenJobsRef.current;
      const justFinished = new Set<string>();
      for (const [id, job] of Object.entries(next)) {
        const prior = prev[id];
        if (
          job.status === "succeeded" &&
          (!prior || prior.id !== job.id || prior.status !== "succeeded")
        ) {
          justFinished.add(id);
        }
      }
      if (justFinished.size > 0) {
        setCatalog((cards) =>
          cards.map((c) => (justFinished.has(c.id) ? withBust(c) : c)),
        );
        setInspect((prev) =>
          prev && justFinished.has(prev.id) ? withBust(prev) : prev,
        );
        setEntries((prev) =>
          prev.map((e) =>
            justFinished.has(e.card.id)
              ? { ...e, card: withBust(e.card) }
              : e,
          ),
        );
      }
    } catch {
      // Background poll — swallow.
    }
  }, []);

  // Poll while any regen-image job is queued/running. Pauses when
  // there's nothing to watch so an idle editor doesn't hammer the API.
  const hasPendingRegen = useMemo(
    () =>
      Object.values(regenJobs).some(
        (j) => j.status === "queued" || j.status === "running",
      ),
    [regenJobs],
  );
  useEffect(() => {
    if (!hasPendingRegen) return;
    const id = setInterval(() => {
      void refreshRegenJobs();
    }, 2500);
    return () => clearInterval(id);
  }, [hasPendingRegen, refreshRegenJobs]);

  async function onRegenImage(card: CatalogCard) {
    if (!card.pool) {
      setError(
        `Cannot regen image: card ${card.id} has no pool/world. Refresh the catalog and try again.`,
      );
      return;
    }
    setError(null);
    setRegenSubmitting(card.id);
    try {
      await api.enqueueRegenImage(card.id);
      await refreshRegenJobs();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setRegenSubmitting(null);
    }
  }

  async function saveDeckOnly(): Promise<AdminNpcDeck | null> {
    if (!canSave || !deck) return null;
    setSaving(true);
    setError(null);
    try {
      const cards: Array<[string, number]> = entries.map((e) => [
        e.card.id,
        e.count,
      ]);
      const payload = {
        npc_id: deck.npc_id,
        name: name.trim(),
        faction,
        cards,
        ...(newSlugPreview ? { slug: newSlugPreview } : {}),
      };
      const { deck: saved } = await api.updateAdminNpcDeck(slug, payload);
      return saved;
    } catch (err) {
      if (
        err instanceof api.ApiError &&
        err.body &&
        typeof err.body === "object" &&
        "details" in err.body
      ) {
        setError(formatChangesetErrors(err.body));
      } else {
        setError(err instanceof Error ? err.message : String(err));
      }
      return null;
    } finally {
      setSaving(false);
    }
  }

  async function onSave() {
    const saved = await saveDeckOnly();
    if (saved) {
      navigate(`/admin/npcs?npc=${encodeURIComponent(saved.npc_id)}`);
    }
  }

  // Snapshot of the loaded deck so we can detect unsaved edits
  // (drives the "save before editing this card?" prompt).
  const isDirty = useMemo(() => {
    if (!deck) return false;
    if (name.trim() !== deck.name) return true;
    if (faction !== (deck.faction ?? "humans")) return true;
    if (entries.length !== deck.cards.length) return true;
    const counts = new Map(deck.cards.map((c) => [c.card_id, c.count]));
    for (const e of entries) {
      if (counts.get(e.card.id) !== e.count) return true;
    }
    return false;
  }, [deck, name, faction, entries]);

  async function onDeleteCard(card: CatalogCard) {
    if (!card.pool) {
      setError(
        `Cannot delete: card ${card.id} has no pool/world. Refresh the catalog and try again.`,
      );
      return;
    }
    const proceed = window.confirm(
      `Delete "${card.name ?? card.id}"?\n\n` +
        "This removes the card directory from disk AND wipes every " +
        "DB row that references it (claims, collections, deck_cards). " +
        "Decks that still reference this card will fail validation on " +
        "next save. Irreversible.",
    );
    if (!proceed) return;
    setError(null);
    try {
      await api.deleteAdminCard(card.id);
      setInspect(null);
      await loadCatalog();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }

  async function onEditCard(card: NpcDeckCatalogCard) {
    if (!card.pool) {
      setError(
        `Cannot edit: card ${card.id} has no pool/world. Refresh the catalog and try again.`,
      );
      return;
    }
    let returnSlug = slug;
    if (isDirty) {
      const ok = window.confirm(
        "Save current deck changes before editing this card?",
      );
      if (!ok) return;
      const saved = await saveDeckOnly();
      if (!saved) return;
      returnSlug = saved.slug ?? slug;
    }
    setInspect(null);
    navigate(`/admin/card-editor/${card.id}/edit`, {
      state: { returnTo: `/admin/npc-decks/${returnSlug}/edit` },
    });
  }

  function onCancel() {
    if (deck) {
      navigate(`/admin/npcs?npc=${encodeURIComponent(deck.npc_id)}`);
    } else {
      navigate("/admin/npcs");
    }
  }

  return (
    <div
      style={{
        height: "100%",
        overflow: "hidden",
        background: theme.pageBg,
        color: theme.ink,
        fontFamily: theme.fontBody,
        padding: "20px 24px",
        display: "flex",
        flexDirection: "column",
        gap: 14,
      }}
    >
      <AppHeader
        user={user}
        backTo="/admin/npcs"
        backLabel="Back to NPC Decks"
      />
      <Breadcrumb text={`ADMIN · NPC DECK${name ? ` · ${name}` : ""}`} />

      {error && (
        <div
          style={{
            padding: 12,
            background: theme.panel,
            border: `2px solid ${theme.accent}`,
            color: theme.accent,
          }}
        >
          {error}
        </div>
      )}

      {loadingDeck || !deck ? (
        <div style={{ color: theme.inkFaint }}>Loading…</div>
      ) : narrow ? (
        <div
          style={{
            display: "grid",
            gridTemplateRows: "auto minmax(0, 1fr)",
            gap: 16,
            flex: 1,
            minHeight: 0,
          }}
        >
          <DeckPanel
            theme={theme}
            compact
            deck={deck}
            name={name}
            setName={setName}
            faction={faction}
            setFaction={setFaction}
            slug={slug}
            newSlugPreview={newSlugPreview}
            entries={entries}
            removeOne={removeCopy}
            onInspect={setInspect}
            total={total}
            issues={validationIssues}
            canSave={canSave}
            saving={saving}
            onSave={onSave}
            onCancel={onCancel}
          />
          <div
            style={{
              background: theme.panel,
              border: `2px solid ${theme.panelEdge}`,
              padding: 12,
              minHeight: 0,
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
            }}
          >
            {loadingCatalog && catalog.length === 0 ? (
              <div style={{ color: theme.inkFaint }}>Loading…</div>
            ) : (
              <>
                <CardBrowser
                  cards={catalog}
                  theme={theme}
                  urlSync
                  serverFiltered
                  onFiltersChange={onBrowserFiltersChange}
                  deckCounts={deckCounts}
                  addPolicy={npcDeckAddPolicy}
                  onAddCopy={onAddFromBrowser}
                  onInspect={onInspectFromBrowser}
                  extraFilterControl={browserExtras}
                />
                <PaginationControls
                  page={page}
                  perPage={PER_PAGE}
                  total={totalCards}
                  theme={theme}
                  onPage={setPage}
                />
              </>
            )}
          </div>
        </div>
      ) : (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "minmax(0, 1fr) 380px",
            gap: 16,
            flex: 1,
            minHeight: 0,
          }}
        >
          <div
            style={{
              background: theme.panel,
              border: `2px solid ${theme.panelEdge}`,
              padding: 12,
              minHeight: 0,
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
            }}
          >
            {loadingCatalog && catalog.length === 0 ? (
              <div style={{ color: theme.inkFaint }}>Loading…</div>
            ) : (
              <>
                <CardBrowser
                  cards={catalog}
                  theme={theme}
                  urlSync
                  serverFiltered
                  onFiltersChange={onBrowserFiltersChange}
                  deckCounts={deckCounts}
                  addPolicy={npcDeckAddPolicy}
                  onAddCopy={onAddFromBrowser}
                  onInspect={onInspectFromBrowser}
                  extraFilterControl={browserExtras}
                />
                <PaginationControls
                  page={page}
                  perPage={PER_PAGE}
                  total={totalCards}
                  theme={theme}
                  onPage={setPage}
                />
              </>
            )}
          </div>
          <DeckPanel
            theme={theme}
            deck={deck}
            name={name}
            setName={setName}
            faction={faction}
            setFaction={setFaction}
            slug={slug}
            newSlugPreview={newSlugPreview}
            entries={entries}
            removeOne={removeCopy}
            onInspect={setInspect}
            total={total}
            issues={validationIssues}
            canSave={canSave}
            saving={saving}
            onSave={onSave}
            onCancel={onCancel}
          />
        </div>
      )}

      <Inspector
        card={inspect}
        theme={theme}
        onClose={() => setInspect(null)}
        addAction={
          inspect
            ? { canAdd: true, onAdd: () => addCopy(inspect) }
            : undefined
        }
        secondaryActions={
          inspect ? (
            <div style={{ display: "flex", gap: 8 }}>
              <button
                onClick={() => void onEditCard(inspect)}
                disabled={!inspect.pool || saving}
                title={
                  !inspect.pool
                    ? "This card has no pool — refresh the catalog and try again."
                    : undefined
                }
                {...withBtnStyle(defaultBtn(theme, !inspect.pool || saving), {
                  fontSize: 11,
                  padding: "8px 16px",
                })}
              >
                EDIT
              </button>
              <RegenImageButton
                theme={theme}
                card={inspect}
                job={regenJobs[inspect.id] ?? null}
                submitting={regenSubmitting === inspect.id}
                onClick={() => onRegenImage(inspect)}
              />
              <button
                onClick={() => void onDeleteCard(inspect)}
                {...withBtnStyle(defaultBtn(theme, false), {
                  fontSize: 11,
                  padding: "8px 16px",
                  background: E32.scarlet,
                  color: E32.parchment,
                  border: `2px solid ${E32.scarlet}`,
                })}
              >
                DELETE
              </button>
            </div>
          ) : null
        }
      />
    </div>
  );
}

function withBust<T extends CatalogCard>(card: T): T {
  if (!card.image_url) return card;
  const sep = card.image_url.includes("?") ? "&" : "?";
  return { ...card, image_url: `${card.image_url}${sep}v=${Date.now()}` };
}

function isPending(job: AdminJob | null): boolean {
  return job != null && (job.status === "queued" || job.status === "running");
}

function RegenImageButton({
  theme,
  card,
  job,
  submitting,
  onClick,
}: {
  theme: Theme;
  card: NpcDeckCatalogCard;
  job: AdminJob | null;
  submitting: boolean;
  onClick: () => void;
}) {
  const pending = submitting || isPending(job);
  return (
    <button
      onClick={onClick}
      disabled={pending || !card.pool}
      title={
        !card.pool
          ? "This card has no pool — refresh the catalog and try again."
          : undefined
      }
      {...withBtnStyle(defaultBtn(theme, pending || !card.pool), {
        fontSize: 11,
        padding: "8px 16px",
      })}
    >
      {pending ? <AnimatedEllipsis label="GENERATING" /> : "REGEN IMAGE"}
    </button>
  );
}


function DeckPanel({
  theme,
  deck,
  name,
  setName,
  faction,
  setFaction,
  slug,
  newSlugPreview,
  entries,
  removeOne,
  onInspect,
  total,
  issues,
  canSave,
  saving,
  onSave,
  onCancel,
  compact = false,
}: {
  theme: Theme;
  deck: AdminNpcDeck;
  name: string;
  setName: (v: string) => void;
  faction: Faction;
  setFaction: (f: Faction) => void;
  slug: string;
  newSlugPreview: string | null;
  entries: DeckEntry[];
  removeOne: (cardId: string) => void;
  onInspect: (card: NpcDeckCatalogCard) => void;
  total: number;
  issues: string[];
  canSave: boolean;
  saving: boolean;
  onSave: () => void;
  onCancel: () => void;
  compact?: boolean;
}) {
  const sorted = useMemo(
    () =>
      entries.slice().sort((a, b) => {
        if (a.card.cost !== b.card.cost) return a.card.cost - b.card.cost;
        return a.card.name.localeCompare(b.card.name);
      }),
    [entries],
  );

  return (
    <aside
      style={{
        background: theme.panel,
        border: `2px solid ${theme.panelEdge}`,
        padding: 14,
        display: "grid",
        gridTemplateRows: compact
          ? "auto auto auto"
          : "auto auto auto minmax(0, 1fr) auto",
        gap: 10,
        minHeight: 0,
      }}
    >
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "minmax(0, 1fr) auto",
          gap: 8,
          alignItems: "end",
        }}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <label
            style={{
              fontFamily: theme.fontDisplay,
              fontSize: 10,
              letterSpacing: theme.letterSpacing,
              color: theme.inkFaint,
            }}
          >
            DECK NAME
          </label>
          <input
            type="text"
            value={name}
            maxLength={200}
            onChange={(e) => setName(e.target.value)}
            style={{
              padding: "8px 10px",
              fontFamily: theme.fontBody,
              fontSize: 16,
              background: theme.panelSolid,
              color: theme.ink,
              border: `2px solid ${theme.panelEdge}`,
            }}
          />
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <label
            style={{
              fontFamily: theme.fontDisplay,
              fontSize: 10,
              letterSpacing: theme.letterSpacing,
              color: theme.inkFaint,
            }}
          >
            FACTION
          </label>
          <select
            value={faction}
            onChange={(e) => setFaction(e.target.value as Faction)}
            style={{
              padding: "8px 10px",
              fontFamily: theme.fontBody,
              fontSize: 14,
              background: theme.panelSolid,
              color: theme.ink,
              border: `2px solid ${theme.panelEdge}`,
            }}
          >
            {FACTIONS.map((f) => (
              <option key={f} value={f}>
                {factionFor(f).name}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          fontSize: 12,
          color: theme.inkFaint,
          fontFamily: "monospace",
        }}
      >
        <span>npc: {deck.npc_id}</span>
        <span style={{ marginLeft: "auto" }}>
          slug:{" "}
          <span style={{ color: theme.ink }}>
            {newSlugPreview ?? slug}
          </span>
          {newSlugPreview && (
            <span style={{ color: theme.accent, marginLeft: 6 }}>
              (renames from {slug})
            </span>
          )}
        </span>
      </div>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
        }}
      >
        <span
          style={{
            fontFamily: theme.fontDisplay,
            fontSize: 10,
            letterSpacing: theme.letterSpacing,
            color: theme.accent,
          }}
        >
          {factionFor(faction).name.toUpperCase()}
        </span>
        <span style={{ marginLeft: "auto", color: theme.inkFaint, fontSize: 13 }}>
          {total}/{DECK_SIZE}
        </span>
      </div>

      <div
        style={{
          overflowY: "auto",
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          gap: 4,
          maxHeight: compact ? 200 : undefined,
        }}
      >
        {sorted.length === 0 ? (
          <div
            style={{
              color: theme.inkFaint,
              padding: "24px 8px",
              textAlign: "center",
            }}
          >
            Click a card to inspect, then tap + to add.
          </div>
        ) : (
          sorted.map((e) => (
            <DeckEntryRow
              key={e.card.id}
              entry={e}
              theme={theme}
              onRemove={() => removeOne(e.card.id)}
              onInspect={() => onInspect(e.card)}
            />
          ))
        )}
      </div>

      <div style={{ display: "grid", gap: 8 }}>
        {issues.length > 0 && (
          <ul
            style={{
              margin: 0,
              padding: "8px 10px 8px 26px",
              color: theme.inkDim,
              fontSize: 13,
              background: theme.panelSolid,
              border: `1px dashed ${theme.panelEdge}`,
              maxHeight: 100,
              overflowY: "auto",
            }}
          >
            {issues.map((m, i) => (
              <li key={i}>{m}</li>
            ))}
          </ul>
        )}
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button
            onClick={onCancel}
            {...withBtnStyle(defaultBtn(theme), {
              fontSize: 11,
              padding: "10px 14px",
            })}
          >
            CANCEL
          </button>
          <button
            onClick={onSave}
            disabled={!canSave}
            {...withBtnStyle(primaryBtn(theme, !canSave), {
              fontSize: 11,
              padding: "10px 16px",
            })}
          >
            {saving ? "SAVING…" : "SAVE"}
          </button>
        </div>
      </div>
    </aside>
  );
}

const RARITY_BORDER: Record<string, string> = {
  common: RARITIES.common.color,
  uncommon: RARITIES.uncommon.color,
  rare: RARITIES.rare.color,
  legendary: RARITIES.legendary.color,
};

function DeckEntryRow({
  entry,
  theme,
  onRemove,
  onInspect,
}: {
  entry: DeckEntry;
  theme: Theme;
  onRemove: () => void;
  onInspect: () => void;
}) {
  const f = factionFor(entry.card.faction);
  const rarityColor =
    RARITY_BORDER[entry.card.rarity] ?? RARITY_BORDER.common;
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "auto 1fr auto auto",
        alignItems: "center",
        gap: 8,
        padding: "0 8px",
        height: 38,
        flexShrink: 0,
        background: theme.panelSolid,
        border: `1px solid ${theme.panelEdge}`,
        borderLeft: `4px solid ${rarityColor}`,
      }}
    >
      <div
        style={{
          minWidth: 22,
          height: 22,
          display: "grid",
          placeItems: "center",
          fontFamily: theme.fontDisplay,
          fontSize: 11,
          color: E32.ink,
          background: f.color,
        }}
        title={f.name}
      >
        {entry.card.cost}
      </div>
      <button
        onClick={onInspect}
        title="Inspect"
        style={{
          background: "transparent",
          border: "none",
          padding: 0,
          textAlign: "left",
          cursor: "pointer",
          color: theme.ink,
          fontFamily: theme.fontBody,
          fontSize: 14,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {entry.card.name}
      </button>
      <span
        style={{
          fontFamily: theme.fontDisplay,
          fontSize: 12,
          color: theme.inkDim,
        }}
      >
        ×{entry.count}
      </span>
      <button
        onClick={onRemove}
        title="Remove one"
        style={{
          fontFamily: theme.fontDisplay,
          fontSize: 10,
          padding: "2px 6px",
          background: theme.panel,
          color: theme.inkDim,
          border: `1px solid ${theme.panelEdge}`,
          cursor: "pointer",
        }}
      >
        −
      </button>
    </div>
  );
}

function isFactionString(s: string | null | undefined): s is Faction {
  return !!s && (FACTIONS as readonly string[]).includes(s);
}

